import { PlannedRide } from "./PlannedRide";
import { Ride } from "../../queries/models/ride.model";
import { PlannedTreatment } from "./PlannedTreatment";
import { Treatment } from "../../queries/models/treatment.model";
import { Rider } from "../../queries/models/rider.model";
import { Horse } from "../../queries/models/horse.model";
import { RideType } from "../../queries/models/ride-type.model";
import { User } from "../../queries/models/user.model";
import { UserRole } from "../../queries/models/enums/user-role";
import { tr } from "date-fns/locale";

const now = new Date();

const sampleUser: User = {
  id: 1,
  name: "Instruktor",
  shortName: "I",
  phone: "",
  email: "",
  color: "#E0F2F1",
  username: "Instruktor",
  roles: UserRole.Instructor,
  roleId: 1,
  isAdmin: false,
  isResetPasswordPending: false,
  assignedInstallationIds: [],
  dateCreated: now,
  dateModified: now,
  isArchived: false,
  includePauseTimeForInvoicing: false,
};

const sampleRider = (useCredits: boolean, credits: number): Rider => ({
  id: 1,
  name: "Jeździec",
  shortName: "J",
  useCredits,
  credits,
  phoneNumber: "",
  age: 25,
  comment: "",
  rides: [],
  isChargeable: true,
});

const sampleHorse: Horse = {
  id: 1,
  name: "Koń",
  shortName: "K",
  comment: "",
};

const sampleRideType: RideType = {
  id: 1,
  name: "Typ",
  order: 1,
  comment: "",
};

export function PlanLegend() {
  const legendData: { label: string; ride: Ride }[] = [
    {
      label:
        "Standardowy widok: kolor tła od instruktora, brak innych znaczników",
      ride: {
        id: 0,
        isPaid: false,
        isCancelled: false,
        hasIssue: false,
        hasFall: false,
        date: now,
        comment: "",
        duration: 1,
        rider: sampleRider(false, 5),
        riderId: 1,
        horse: sampleHorse,
        horseId: 1,
        rideType: sampleRideType,
        rideTypeId: 1,
        user: sampleUser,
        userId: 1,
        isHorseExhausted: false,
        isScheduled: true,
        hasPreviousHorseIssues: false,
      },
    },
    {
      label: "Brak instruktora (tekst bez 'Instruktor' i jego koloru)",
      ride: {
        id: 8,
        isPaid: false,
        isCancelled: false,
        hasIssue: false,
        hasFall: false,
        date: now,
        comment: "",
        duration: 1,
        rider: sampleRider(true, 2),
        riderId: 1,
        horse: sampleHorse,
        horseId: 1,
        rideType: sampleRideType,
        rideTypeId: 1,
        user: undefined,
        userId: undefined,
        isHorseExhausted: false,
        isScheduled: true,
        hasPreviousHorseIssues: false,
      },
    },
    {
      label: "Jazda trwajaca więcej niż 1 godzinę: ikona piaskowego zegara",
      ride: {
        id: 0,
        isPaid: false,
        isCancelled: false,
        hasIssue: false,
        hasFall: false,
        date: now,
        comment: "",
        duration: 2,
        rider: sampleRider(false, 5),
        riderId: 1,
        horse: sampleHorse,
        horseId: 1,
        rideType: sampleRideType,
        rideTypeId: 1,
        user: sampleUser,
        userId: 1,
        isHorseExhausted: false,
        isScheduled: true,
        hasPreviousHorseIssues: false,
      },
    },
    {
      label:
        "Czerwona ramka: wykorzystany karnet, brak kredytów (jazda idzie na minus)",
      ride: {
        id: 1,
        isPaid: false,
        isCancelled: false,
        hasIssue: false,
        hasFall: false,
        date: now,
        comment: "",
        duration: 1,
        rider: sampleRider(true, 0),
        riderId: 1,
        horse: sampleHorse,
        horseId: 1,
        rideType: sampleRideType,
        rideTypeId: 1,
        user: sampleUser,
        userId: 1,
        isHorseExhausted: false,
        isScheduled: true,
        hasPreviousHorseIssues: false,
      },
    },
    {
      label: "Niebieski tekst: koń dwa razy na tej samej jeździe",
      ride: {
        id: 2,
        isPaid: false,
        isCancelled: false,
        hasIssue: false,
        hasFall: false,
        date: now,
        comment: "Koń dwa razy na tej samej jeździe",
        duration: 1,
        rider: sampleRider(true, 2),
        riderId: 1,
        horse: sampleHorse,
        horseId: 1,
        rideType: sampleRideType,
        rideTypeId: 1,
        user: sampleUser,
        userId: 1,
        isHorseExhausted: true, // Traktujemy to jako jeden z przypadków zmęczenia
        isScheduled: true,
        hasPreviousHorseIssues: false,
      },
    },
    {
      label: "Niebieski tekst: koń ma X jazd pod rząd",
      ride: {
        id: 3,
        isPaid: false,
        isCancelled: false,
        hasIssue: false,
        hasFall: false,
        date: now,
        comment: "Koń ma 3 jazdy pod rząd",
        duration: 1,
        rider: sampleRider(true, 2),
        riderId: 1,
        horse: sampleHorse,
        horseId: 1,
        rideType: sampleRideType,
        rideTypeId: 1,
        user: sampleUser,
        userId: 1,
        isHorseExhausted: true,
        isScheduled: true,
        hasPreviousHorseIssues: false,
      },
    },
    {
      label: "Niebieski tekst: koń ma X jazd w ciągu dnia",
      ride: {
        id: 4,
        isPaid: false,
        isCancelled: false,
        hasIssue: false,
        hasFall: false,
        date: now,
        comment: "Koń ma 4 jazdy tego dnia",
        duration: 1,
        rider: sampleRider(true, 2),
        riderId: 1,
        horse: sampleHorse,
        horseId: 1,
        rideType: sampleRideType,
        rideTypeId: 1,
        user: sampleUser,
        userId: 1,
        isHorseExhausted: true,
        isScheduled: true,
        hasPreviousHorseIssues: false,
      },
    },
    {
      label:
        "Pomarańczowa czcionka: jeździec miał wcześniej problem z tym koniem",
      ride: {
        id: 5,
        isPaid: false,
        isCancelled: false,
        hasIssue: false,
        hasFall: false,
        date: now,
        comment: "Historia problemów",
        duration: 1,
        rider: sampleRider(true, 2),
        riderId: 1,
        horse: sampleHorse,
        horseId: 1,
        rideType: sampleRideType,
        rideTypeId: 1,
        user: sampleUser,
        userId: 1,
        isHorseExhausted: false,
        isScheduled: true,
        hasPreviousHorseIssues: true,
      },
    },
    {
      label: "Zielona ramka: jazda opłacona",
      ride: {
        id: 6,
        isPaid: true,
        isCancelled: false,
        hasIssue: false,
        hasFall: false,
        date: now,
        comment: "",
        duration: 1,
        rider: sampleRider(false, 0),
        riderId: 1,
        horse: sampleHorse,
        horseId: 1,
        rideType: sampleRideType,
        rideTypeId: 1,
        user: sampleUser,
        userId: 1,
        isHorseExhausted: false,
        isScheduled: true,
        hasPreviousHorseIssues: false,
      },
    },
    {
      label: "Czerwona czcionka: jazda odwołana",
      ride: {
        id: 7,
        isPaid: false,
        isCancelled: true,
        hasIssue: false,
        hasFall: false,
        date: now,
        comment: "",
        duration: 1,
        rider: sampleRider(true, 2),
        riderId: 1,
        horse: sampleHorse,
        horseId: 1,
        rideType: sampleRideType,
        rideTypeId: 1,
        user: sampleUser,
        userId: 1,
        isHorseExhausted: false,
        isScheduled: true,
        hasPreviousHorseIssues: false,
      },
    },
    {
      label: "Brązowa czcionka: upadek podczas jazdy",
      ride: {
        id: 7,
        isPaid: false,
        isCancelled: false,
        hasIssue: false,
        hasFall: true,
        date: now,
        comment: "",
        duration: 1,
        rider: sampleRider(true, 2),
        riderId: 1,
        horse: sampleHorse,
        horseId: 1,
        rideType: sampleRideType,
        rideTypeId: 1,
        user: sampleUser,
        userId: 1,
        isHorseExhausted: false,
        isScheduled: true,
        hasPreviousHorseIssues: false,
      },
    },
  ];

  return (
    <div className="p-4">
      {legendData.map(({ label, ride }, idx) => (
        <div key={idx} className="mb-4">
          <div className="text-sm font-medium mb-1">{label}</div>
          <PlannedRide
            ride={ride}
            shortNames={false}
            showInstructor={true}
            onSelected={() => {}}
          />
        </div>
      ))}
      <div className="mb-4">
        <div className="text-sm font-medium mb-1">Zabieg weterynaryjny</div>
        <PlannedTreatment treatment={{ id: 0, date: now, treatmentTypeId: 1, treatmentType: { id: 1, name: "Weterynarz", order: 1, comment: "" }, comment: "", horseTreatments: [{ id: 1, horseId: 1, comment: "", horse: sampleHorse }] } as unknown as Treatment} />
      </div>
    </div>
  );
}
